import React, { createContext, useContext, useId, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';

const CSS_VAR_PREFIX = 'bc';

const CssVarContext = createContext<string>('css-var-bc-theme');

function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

export interface BcThemeProviderProps {
  theme: ThemeConfig;
  customTokens?: Record<string, string>;
  children: React.ReactNode;
}

export const ThemeProvider = ({ theme, customTokens, children }: BcThemeProviderProps) => {
  const cssVarKey = `bc-${useId().replace(/:/g, '')}`;

  const mergedTheme = useMemo(
    () => ({ ...theme, cssVar: { prefix: CSS_VAR_PREFIX, key: cssVarKey } }),
    [theme, cssVarKey],
  );

  const customVarStyle = useMemo(() => {
    if (!customTokens) return undefined;
    const style: Record<string, string> = { display: 'contents' };
    for (const [key, value] of Object.entries(customTokens)) {
      style[`--${CSS_VAR_PREFIX}-${camelToKebab(key)}`] = value;
    }
    return style as React.CSSProperties;
  }, [customTokens]);

  return (
    <ConfigProvider theme={mergedTheme}>
      <CssVarContext.Provider value={cssVarKey}>
        {customVarStyle
          ? <div style={customVarStyle}>{children}</div>
          : children}
      </CssVarContext.Provider>
    </ConfigProvider>
  );
};

export const useThemeVarsClass = () => useContext(CssVarContext);
